import React from 'react';

// Styles injected once into the DOM
if (typeof document !== 'undefined' && !document.getElementById('nemesis-face-styles')) {
    const style = document.createElement('style');
    style.id = 'nemesis-face-styles';
    style.textContent = `
        @keyframes nf-idle { 0%,100%{transform:scale(1) translateY(0);opacity:.92} 50%{transform:scale(1.018) translateY(-3px);opacity:1} }
        @keyframes nf-speaking { 0%,100%{transform:scale(1);opacity:.95} 30%{transform:scale(1.025);opacity:1} 70%{transform:scale(1.01);opacity:.97} }
        @keyframes nf-flicker { 0%,100%{opacity:1} 10%{opacity:.5} 25%{opacity:.85} 40%{opacity:.38} 60%{opacity:.92} 80%{opacity:.55} }
        @keyframes nf-winning { 0%,100%{transform:scale(1);opacity:.9} 50%{transform:scale(1.03);opacity:1} }
        @keyframes nf-shake { 0%,100%{transform:translate(0,0) rotate(0deg)} 12%{transform:translate(-5px,2px) rotate(-2deg)} 28%{transform:translate(5px,-2px) rotate(2deg)} 44%{transform:translate(-3px,1px) rotate(-1deg)} 60%{transform:translate(3px,-1px) rotate(.5deg)} 76%{transform:translate(-2px,1px)} }
        @keyframes nf-damage { 0%,100%{filter:none} 30%{filter:brightness(2.5) sepia(1) hue-rotate(310deg)} }
        @keyframes nf-ring-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes nf-pulse-ring { 0%,100%{opacity:.3} 50%{opacity:.7} }
        @keyframes nf-eye-blink { 0%,90%,100%{transform:scaleY(1)} 95%{transform:scaleY(0.08)} }
        @keyframes nf-dead-fade { 0%{opacity:1} 100%{opacity:.2} }
    `;
    document.head.appendChild(style);
}

const CRACK_PATHS = [
    'M 112 83 L 132 62 L 150 50 L 174 32',
    'M 120 103 L 145 96 L 172 101 L 192 93',
    'M 87 117 L 66 138 L 48 150 L 25 170',
    'M 106 124 L 100 150 L 109 173 L 103 194',
    'M 86 85 L 63 62 L 44 56 L 20 37',
];

const THEME = {
    idle:     { primary: '#059669', glow: '#10b981',  eye: '#6ee7b7', bg: '#04150f', ring: '#065f46' },
    speaking: { primary: '#0d9488', glow: '#2dd4bf',  eye: '#99f6e4', bg: '#041512', ring: '#115e59' },
    damaged:  { primary: '#b91c1c', glow: '#ef4444',  eye: '#fca5a5', bg: '#140404', ring: '#7f1d1d' },
    critical: { primary: '#dc2626', glow: '#f87171',  eye: '#fecaca', bg: '#150505', ring: '#991b1b' },
    winning:  { primary: '#b45309', glow: '#f59e0b',  eye: '#fde68a', bg: '#110a00', ring: '#92400e' },
    dead:     { primary: '#374151', glow: '#4b5563',  eye: '#6b7280', bg: '#060608', ring: '#1f2937' },
};

export default function NemesisFace({ hp = 100, maxHp = 100, nemesisState = 'idle', size = 200, className = '' }) {
    const pct = maxHp > 0 ? Math.max(0, hp) / maxHp : 0;
    const numCracks = [0.79, 0.59, 0.39, 0.19, 0.04].filter(t => pct <= t).length;

    const isDead = nemesisState === 'dead';
    const isCritical = nemesisState === 'critical' || (!isDead && pct < 0.2);
    const isWinning = nemesisState === 'winning';
    const isDamaged = nemesisState === 'damaged';
    const isSpeaking = nemesisState === 'speaking';

    const themeKey = isDead ? 'dead' : isWinning ? 'winning' : isCritical ? 'critical' : isDamaged ? 'damaged' : isSpeaking ? 'speaking' : 'idle';
    const t = THEME[themeKey];

    const svgAnim = isDead
        ? 'nf-dead-fade 1.5s ease-out forwards'
        : isDamaged
        ? 'nf-shake 0.45s ease-in-out, nf-damage 0.45s ease-in-out'
        : isCritical
        ? 'nf-flicker 0.18s ease-in-out infinite'
        : isWinning
        ? 'nf-winning 1.3s ease-in-out infinite'
        : isSpeaking
        ? 'nf-speaking 0.55s ease-in-out infinite'
        : 'nf-idle 3.5s ease-in-out infinite';

    // Eyebrow/pupil shape changes by state
    const pupilRy = isWinning ? 3 : isDead ? 2 : 14;
    const pupilRx = isDead ? 2 : 11;
    const eyeOpacity = isDead ? 0.3 : 1;

    return (
        <div className={`inline-block ${className}`} style={{ width: size, height: size }}>
            <svg
                viewBox="0 0 200 200"
                width={size}
                height={size}
                style={{
                    filter: `drop-shadow(0 0 10px ${t.glow}) drop-shadow(0 0 28px ${t.glow}55)`,
                    animation: svgAnim,
                    transition: 'filter 0.6s ease',
                    transformOrigin: 'center center',
                    display: 'block',
                }}
            >
                <defs>
                    {/* Glow filter for elements */}
                    <filter id="nf-glow" x="-40%" y="-40%" width="180%" height="180%">
                        <feGaussianBlur stdDeviation="3.5" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <filter id="nf-strong-glow" x="-60%" y="-60%" width="220%" height="220%">
                        <feGaussianBlur stdDeviation="7" result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    {/* Face background gradient */}
                    <radialGradient id="nf-bg" cx="50%" cy="42%" r="58%">
                        <stop offset="0%" stopColor={t.primary} stopOpacity="0.18" />
                        <stop offset="100%" stopColor={t.bg} stopOpacity="1" />
                    </radialGradient>
                    {/* Eye inner glow gradient */}
                    <radialGradient id="nf-eye-grad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor={t.eye} stopOpacity="0.5" />
                        <stop offset="100%" stopColor={t.eye} stopOpacity="0" />
                    </radialGradient>
                    {/* Scanlines */}
                    <pattern id="nf-scan" x="0" y="0" width="200" height="3" patternUnits="userSpaceOnUse">
                        <rect x="0" y="0" width="200" height="1.2" fill="rgba(0,0,0,0.22)" />
                    </pattern>
                    {/* Clip to circle */}
                    <clipPath id="nf-circ"><circle cx="100" cy="100" r="91" /></clipPath>
                </defs>

                {/* ── Base fill ── */}
                <circle cx="100" cy="100" r="92" fill={t.bg} />
                <circle cx="100" cy="100" r="92" fill="url(#nf-bg)" />

                {/* ── Outer rings ── */}
                <circle cx="100" cy="100" r="92" fill="none" stroke={t.primary} strokeWidth="2.5" opacity="0.9" filter="url(#nf-glow)" />
                <circle cx="100" cy="100" r="86" fill="none" stroke={t.primary} strokeWidth="0.6" opacity="0.4" />
                <circle cx="100" cy="100" r="79" fill="none" stroke={t.primary} strokeWidth="0.4" opacity="0.2" />

                {/* ── Tick marks (24 ticks around rim) ── */}
                {Array.from({ length: 24 }).map((_, i) => {
                    const a = (i / 24) * 2 * Math.PI - Math.PI / 2;
                    const r1 = 80, r2 = i % 6 === 0 ? 88 : i % 3 === 0 ? 85 : 82;
                    return (
                        <line key={i}
                            x1={100 + r1 * Math.cos(a)} y1={100 + r1 * Math.sin(a)}
                            x2={100 + r2 * Math.cos(a)} y2={100 + r2 * Math.sin(a)}
                            stroke={t.primary}
                            strokeWidth={i % 6 === 0 ? 1.8 : i % 3 === 0 ? 1 : 0.6}
                            opacity={i % 6 === 0 ? 0.8 : 0.25}
                        />
                    );
                })}

                {/* ── Inner face panel ── */}
                <circle cx="100" cy="100" r="70" fill={t.bg} fillOpacity="0.85" />
                <circle cx="100" cy="100" r="70" fill="none" stroke={t.primary} strokeWidth="1" opacity="0.3" strokeDasharray="5 8" />

                {/* ── Spinning outer decoration ring (slow) ── */}
                <g style={{ transformOrigin: '100px 100px', animation: isDead ? 'none' : 'nf-ring-spin 18s linear infinite' }}>
                    {Array.from({ length: 6 }).map((_, i) => {
                        const a = (i / 6) * 2 * Math.PI;
                        return <circle key={i} cx={100 + 76 * Math.cos(a)} cy={100 + 76 * Math.sin(a)} r="2.5" fill={t.primary} opacity="0.5" />;
                    })}
                </g>

                {/* ── THE EYE (diamond shape) ── */}
                <g style={{ transformOrigin: '100px 100px', opacity: eyeOpacity, transition: 'opacity 0.5s ease' }}>
                    {/* Eye outer glow */}
                    <circle cx="100" cy="100" r="30" fill="url(#nf-eye-grad)" filter="url(#nf-strong-glow)" />

                    {/* Diamond frame */}
                    <polygon
                        points="100,60 142,100 100,140 58,100"
                        fill={t.bg}
                        stroke={t.eye}
                        strokeWidth="1.8"
                        opacity="0.95"
                        filter="url(#nf-glow)"
                    />

                    {/* Iris rings */}
                    <circle cx="100" cy="100" r="26" fill="none" stroke={t.eye} strokeWidth="1.5" opacity="0.5" />
                    <circle cx="100" cy="100" r="20" fill="none" stroke={t.eye} strokeWidth="0.7" opacity="0.3" />

                    {/* Pupil with blink animation */}
                    <ellipse cx="100" cy="100"
                        rx={pupilRx}
                        ry={pupilRy}
                        fill={t.eye}
                        filter="url(#nf-strong-glow)"
                        style={{ transition: 'rx 0.4s ease, ry 0.4s ease', animation: isDead ? 'none' : 'nf-eye-blink 6s ease-in-out infinite' }}
                    />

                    {/* Bright core */}
                    <circle cx="100" cy="100" r="4.5"
                        fill="white"
                        opacity={isDead ? 0 : isSpeaking ? 1 : 0.82}
                        style={{ transition: 'opacity 0.3s ease' }}
                    />
                    {/* Specular highlight */}
                    <circle cx="108" cy="91" r="2.8" fill="white" opacity={isDead ? 0 : 0.45} style={{ transition: 'opacity 0.3s ease' }} />
                </g>

                {/* ── Scanlines overlay ── */}
                <rect x="8" y="8" width="184" height="184" fill="url(#nf-scan)" clipPath="url(#nf-circ)" opacity="0.7" />

                {/* ── Cracks (revealed progressively as HP drops) ── */}
                {CRACK_PATHS.slice(0, numCracks).map((d, i) => (
                    <path key={i} d={d}
                        fill="none"
                        stroke={isCritical ? '#fca5a5' : '#ddd6fe'}
                        strokeWidth={1.2 - i * 0.05}
                        opacity={0.55 + i * 0.09}
                        strokeLinecap="round"
                        filter="url(#nf-glow)"
                    />
                ))}

                {/* ── Dead overlay ── */}
                {isDead && (
                    <circle cx="100" cy="100" r="92" fill="#000" opacity="0.65" />
                )}
            </svg>
        </div>
    );
}
